// FILE: src/pages/admin/AdminCurriculaPage.jsx
// Curricula (WP-5; MTO Preface, CD2.4; matrix 0.7, 11.2): every version with
// its status (draft / approved / retired, as icon + words), MTO approval
// reference, lessons, core hours and its last check against the MTO rules.
// At most three curricula are approved at once; a student stays on the one
// they started. Also the "New curriculum" page (a draft).
//
//   GET  /api/admin/curricula
//   POST /api/admin/curricula   { name, mtoApprovalRef? } → 201 a DRAFT
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Link,
  Stack,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import fetchWithAuth from "../../utils/fetchWithAuth";
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from "../../components/ui";
import { ResponsiveTable } from "../../components/admin/AdminBits";
import { CheckStatusChip, CurriculumStatusChip } from "../../components/admin/ContentBits";
import { LIMITS, MAX_APPROVED, curriculumName, problemList } from "../../components/admin/content";
import { errorPayload } from "../../components/admin/apiError";
import { formatDate, formatDateTime, formatNumber } from "../../components/admin/format";
import { errorCode, errorMessage } from "../../i18n/errors";
import { useCan } from "../../hooks/usePermissions";

const LIST = "/admin/curricula";

/** 1250 → "1,250 min (20.8 h)". */
export function minutesText(minutes) {
  const m = Number(minutes) || 0;
  return `${formatNumber(m, m % 1 ? 1 : 0)} min (${formatNumber(m / 60, 1)} h)`;
}

/** The stored result of the last check, as a chip + date. */
export function LastCheck({ validation }) {
  if (!validation || !validation.checkedAt) return <Typography sx={{ color: "text.secondary" }}>Not checked yet</Typography>;
  return (
    <Stack spacing={0.5} sx={{ alignItems: "flex-start" }}>
      <CheckStatusChip status={validation.ok ? "PASS" : "FAIL"} />
      <Typography sx={{ color: "text.secondary" }}>{formatDateTime(validation.checkedAt)}</Typography>
    </Stack>
  );
}

export default function AdminCurriculaPage() {
  const location = useLocation();
  const [notice, setNotice] = useState(location.state?.notice || "");
  const [state, setState] = useState({ list: null, loading: true, error: null });
  const latest = useRef(0);
  const canManage = useCan("curricula.manage");

  const load = useCallback(() => {
    const id = ++latest.current;
    setState((s) => ({ ...s, loading: true, error: null }));
    fetchWithAuth("/api/admin/curricula")
      .then((list) => {
        if (id === latest.current) setState({ list: Array.isArray(list) ? list : [], loading: false, error: null });
      })
      .catch((error) => {
        if (id === latest.current) setState((s) => ({ ...s, loading: false, error: error || "INTERNAL_ERROR" }));
      });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const { list, error } = state;
  const approved = (list || []).filter((c) => c.status === "APPROVED").length;
  const newButton = (children, props = {}) =>
    canManage ? (
      <Button component={RouterLink} to={`${LIST}/new`} variant="contained" {...props}>
        {children}
      </Button>
    ) : null;

  return (
    <Box>
      <PageHeader
        title="Curricula"
        description={`Each version of the course content. Lessons can only be changed in a draft; an approved version stays exactly as MTO approved it. Up to ${MAX_APPROVED} curricula can be approved at a time, and every student finishes on the one they started.`}
        actions={newButton("New curriculum", { startIcon: <AddIcon /> })}
      />

      {notice && (
        <Alert severity="success" role="status" sx={{ mb: 2.5 }} onClose={() => setNotice("")}>
          {notice}
        </Alert>
      )}

      <Section
        id="curricula"
        title="All versions"
        description={list ? `${approved} of ${MAX_APPROVED} approved curricula in use.` : undefined}
        contentSx={{ p: { xs: 2, sm: 3 } }}
      >
        {list === null && error ? (
          <ErrorState error={error} title="We couldn't load the curricula" onRetry={load} />
        ) : list === null ? (
          <LoadingState variant="table" rows={3} label="Loading curricula…" />
        ) : list.length === 0 ? (
          <EmptyState
            icon={AccountTreeOutlinedIcon}
            title="No curricula yet"
            description="Create a draft curriculum and add its lessons, or run the content migration to record the lessons that are live today as the approved curriculum."
            action={newButton("New curriculum")}
          />
        ) : (
          <ResponsiveTable label="Curricula" minWidth={860}>
            <TableHead>
              <TableRow>
                <TableCell component="th" scope="col">
                  Curriculum
                </TableCell>
                <TableCell component="th" scope="col">
                  Status
                </TableCell>
                <TableCell component="th" scope="col">
                  MTO approval reference
                </TableCell>
                <TableCell component="th" scope="col" align="right">
                  Lessons
                </TableCell>
                <TableCell component="th" scope="col">
                  Core lessons
                </TableCell>
                <TableCell component="th" scope="col">
                  Last MTO check
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {list.map((c) => (
                <TableRow key={c.id} hover>
                  <TableCell component="th" scope="row">
                    <Link component={RouterLink} to={`${LIST}/${c.id}`} sx={{ fontWeight: 600 }}>
                      {curriculumName(c)}
                    </Link>
                    {c.approvedAt && <Typography sx={{ color: "text.secondary" }}>Approved {formatDate(c.approvedAt)}</Typography>}
                  </TableCell>
                  <TableCell>
                    <CurriculumStatusChip status={c.status} />
                  </TableCell>
                  <TableCell>{c.mtoApprovalRef || "—"}</TableCell>
                  <TableCell align="right">{(c.resources || []).length}</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{minutesText(c.totalCoreMinutes)}</TableCell>
                  <TableCell>
                    <LastCheck validation={c.validation} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </ResponsiveTable>
        )}
      </Section>
    </Box>
  );
}

/** /admin/curricula/new: a new draft curriculum (name + optional MTO approval reference). */
export function AdminCurriculumNewPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", mtoApprovalRef: "" });
  const [problems, setProblems] = useState([]);
  const [saveError, setSaveError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const alertRef = useRef(null);
  const nameRef = useRef(null);

  const errors = Object.fromEntries(problemList(problems).map((it) => [it.field, it.message]));

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    setProblems((ps) => ps.filter((p) => p.field !== name));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaveError(null);
    const found = [];
    const name = form.name.trim();
    const ref = form.mtoApprovalRef.trim();
    if (!name) found.push({ field: "name", problem: "required" });
    else if (name.length > LIMITS.curriculumText) found.push({ field: "name", problem: "too long" });
    if (ref.length > LIMITS.curriculumText) found.push({ field: "mtoApprovalRef", problem: "too long" });
    setProblems(found);
    if (found.length) {
      nameRef.current?.focus();
      return;
    }
    setSubmitting(true);
    try {
      const c = await fetchWithAuth("/api/admin/curricula", { method: "POST", body: JSON.stringify({ name, ...(ref ? { mtoApprovalRef: ref } : {}) }) });
      navigate(`${LIST}/${c.id}`, { state: { notice: `${curriculumName(c)} was created as a draft. Add its lessons next.` } });
    } catch (err) {
      const body = errorPayload(err);
      if (errorCode(err) === "VALIDATION_FAILED" && Array.isArray(body.problems)) setProblems(body.problems);
      if (errorCode(err) === "MISSING_REQUIRED_FIELDS") setProblems((body.required || ["name"]).map((field) => ({ field, problem: "required" })));
      setSaveError(err || "INTERNAL_ERROR");
      setTimeout(() => alertRef.current?.focus(), 0);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box>
      <PageHeader
        title="New curriculum"
        breadcrumbs={[{ label: "Curricula", to: LIST }, { label: "New curriculum" }]}
        description="A new curriculum starts as a draft. Add its lessons, check it against the MTO rules, then approve it with the MTO approval reference."
      />
      <Box component="form" noValidate onSubmit={handleSubmit} aria-label="New curriculum" sx={{ maxWidth: 720 }}>
        <Stack spacing={2.5}>
          {saveError && (
            <Alert severity="error" ref={alertRef} tabIndex={-1} role="alert">
              <AlertTitle>We couldn't create the curriculum</AlertTitle>
              {errorMessage(saveError)}
            </Alert>
          )}
          <Section id="curriculum-details" title="Curriculum details">
            <Stack spacing={2.5}>
              <TextField
                id="name"
                name="name"
                label="Name"
                value={form.name}
                onChange={handleChange}
                inputRef={nameRef}
                error={!!errors.name}
                helperText={errors.name || "The course name, e.g. Zun Driving School BDE. Versions are numbered for you."}
                required
                fullWidth
              />
              <TextField
                id="mtoApprovalRef"
                name="mtoApprovalRef"
                label="MTO approval reference"
                value={form.mtoApprovalRef}
                onChange={handleChange}
                error={!!errors.mtoApprovalRef}
                helperText={errors.mtoApprovalRef || "Optional now. It's needed to approve the curriculum."}
                fullWidth
              />
            </Stack>
          </Section>
          <Stack direction={{ xs: "column-reverse", sm: "row" }} spacing={1.5} sx={{ justifyContent: "flex-end" }}>
            <Button component={RouterLink} to={LIST} variant="outlined">
              Back to curricula
            </Button>
            <Button type="submit" variant="contained" disabled={submitting}>
              {submitting ? "Creating…" : "Create draft"}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
}
